/**
 * THE BAR, ON THE PAGES THAT HAVE NONE (Gerónimo, 29 Oct 2026: «la barra en
 * todas»). Tickets, Tasks, Calendar, Quick Access, Resources, Admin and the
 * screens with the handover palette have their own floating bar, which
 * already hands requests to Kevo (components/KevoSpark). Everywhere else —
 * Reports, Planner, the rest of Actes… — this one: a client or a ticket
 * number takes you to the case, a request or the ✦ opens Kevo on the right.
 * Only where Kevo is here, and never over another bar (it looks for one on
 * screen) or over the Inbox, whose chat and mail have their own box at the
 * bottom and Kevo's own assistant.
 */
import { useEffect, useState } from 'react';
import type { Lang } from '@/data/types';
import { FloatSearch } from '@/components/FloatSearch';
import type { Suggestion } from '@/components/SearchSuggest';
import { searchTicketsSmart, type TicketMatch } from '@/integrations/hubspot';
import { askKevo, useKevo } from '@/lib/kevoAsk';
import { setOpenCase } from '@/hooks/useOpenCase';

const T = (lang: Lang, en: string, es: string, fr: string, pt: string) =>
  lang === 'es' ? es : lang === 'fr' ? fr : lang === 'pt' ? pt : en;

/* Another page's own floating bar, on screen. */
const OTHER_BARS = '.mw-search-row, .fsearch, .dash-fsearch, .hpal';
function useOtherBar(): boolean {
  const [other, setOther] = useState(false);
  useEffect(() => {
    let raf = 0;
    const check = () => {
      raf = 0;
      setOther([...document.querySelectorAll(OTHER_BARS)].some((el) => !el.closest('.kevo-bar') && el.getClientRects().length > 0));
    };
    const mo = new MutationObserver(() => { if (!raf) raf = requestAnimationFrame(check); });
    mo.observe(document.body, { childList: true, subtree: true, attributes: true, attributeFilter: ['class', 'hidden', 'style'] });
    check();
    return () => { mo.disconnect(); if (raf) cancelAnimationFrame(raf); };
  }, []);
  return other;
}

export function KevoBar({ lang, country, hidden }: { lang: Lang; country: string; hidden: boolean }) {
  const kevo = useKevo();
  const other = useOtherBar();
  const [q, setQ] = useState('');
  const [hits, setHits] = useState<TicketMatch[]>([]);
  const term = q.trim();
  useEffect(() => {
    if (term.length < 2) { setHits([]); return; }
    const ac = new AbortController();
    const t = window.setTimeout(() => {
      searchTicketsSmart(country, term, ac.signal).then((r) => setHits(r.slice(0, 6))).catch(() => { /* aborted, or no answer: no suggestions */ });
    }, 250);
    return () => { window.clearTimeout(t); ac.abort(); };
  }, [term, country]);

  if (!kevo.here || hidden || other) return null;
  const clear = () => { setQ(''); setHits([]); };
  const open = (id: string) => { clear(); setOpenCase(id); };
  const items: Suggestion[] = hits.map((h) => ({
    key: h.id,
    icon: 'ticket',
    label: h.client || h.subject,
    sub: [h.service, h.stageLabel].filter(Boolean).join(' · '),
    tone: 'item',
    onPick: () => open(h.id),
  }));
  return (
    <div className="kevo-bar">
      <FloatSearch
        lang={lang}
        value={q}
        onChange={setQ}
        onClear={clear}
        /* Enter without a suggestion picked: a ticket number opens it,
           anything else is Kevo's (FloatSearch already sent a request). */
        onAsk={() => { if (/^#?\d{5,}$/.test(term)) open(term.replace('#', '')); else if (askKevo(term)) clear(); }}
        asking={false}
        askOn={false}
        suggestions={{ items }}
        hint={T(lang,
          'A client or a ticket number — or ask Kevo: «draft a reply to the last e-mail»',
          'Un cliente o un nº de ticket — o pide a Kevo: «redacta una respuesta al último correo»',
          'Un client ou un nº de ticket — ou demandez à Kevo : « rédige une réponse au dernier e-mail »',
          'Um cliente ou um nº de ticket — ou peça ao Kevo')}
        placeholder={T(lang, 'Client, ticket number, or ask Kevo…', 'Cliente, nº de ticket, o pide a Kevo…', 'Client, nº de ticket, ou demandez à Kevo…', 'Cliente, nº de ticket, ou peça ao Kevo…')}
        askLabel={T(lang, 'Ask Kevo', 'Preguntar a Kevo', 'Demander à Kevo', 'Perguntar ao Kevo')}
      />
    </div>
  );
}
